import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { UserDropdown } from "@/components/UserDropdown";
import logo from "@/assets/ABV-International.webp";

const Navbar = () => {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();

  const navLinks = [
    { name: t("nav.home"), path: "/" },
    { name: t("howItWorks.title"), path: "/#how-it-works" },
    { name: t("services.title"), path: "/#services" },
    { name: t("about.title"), path: "/#about" },
    { name: t("aboutArabookverse.title"), path: "/#about-arabookverse" },
    { name: t("contact.title"), path: "/#contact" },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-border/30 shadow-sm">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/">
            <div className="flex items-center gap-3">
              <img src={logo} alt="ArabOOkverse" className="h-8 w-auto" />
              <div className="w-px h-7 bg-border" aria-hidden="true" />
              <img src="/alc-logo-header.png" alt="ALC" className="h-12 w-auto" />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.path}
                className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
              >
                {link.name}
              </a>
            ))}

            <LanguageSwitcher />

            {isAuthenticated ? (
              <UserDropdown align="end" />
            ) : (
              /* Login/Register Button for Unauthenticated Users */
              <Button variant="hero" size="sm" asChild>
                <Link to="/login">{t("nav.loginRegister")}</Link>
              </Button>
            )}
          </div>

          {/* Mobile Menu */}
          <div className="flex items-center gap-2 md:hidden">
            {isAuthenticated && <UserDropdown align="end" />}

            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="h-6 w-6" />
                  <span className="sr-only">Toggle menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right">
                <SheetHeader>
                  <SheetTitle>{t("nav.menu")}</SheetTitle>
                  <SheetDescription>
                    {t("nav.menuDescription")}
                  </SheetDescription>
                </SheetHeader>
                <div className="flex flex-col gap-4 mt-8">
                  {navLinks.map((link) => (
                    <a
                      key={link.name}
                      href={link.path}
                      className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                    >
                      {link.name}
                    </a>
                  ))}
                  <LanguageSwitcher />
                  {!isAuthenticated && (
                    <Button variant="hero" size="sm" className="w-full mt-4" asChild>
                      <Link to="/login">{t("nav.loginRegister")}</Link>
                    </Button>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
